import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Activity as ActivityIcon, AlertCircle, CheckCircle, Info } from "lucide-react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { ActivityEvent } from "@/api/generated/client";
import { useLocation } from "wouter";
import { getNotificationClaimId } from "@/utils/notification.utils";

interface RecentActivitiesProps {
    activities: ActivityEvent[];
}

export function RecentActivities({ activities }: RecentActivitiesProps) {
    const [, setLocation] = useLocation();

    const getSeverityIcon = (severity: string) => {
        switch (severity) {
            case 'error':
                return <AlertCircle className="h-4 w-4 text-destructive" />;
            case 'warning':
                return <AlertCircle className="h-4 w-4 text-amber-500" />;
            case 'success':
                return <CheckCircle className="h-4 w-4 text-green-600" />;
            default:
                return <Info className="h-4 w-4 text-primary" />;
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="h-full min-w-0"
        >
            <Card className="h-[480px] min-w-0 flex flex-col overflow-hidden">
                <CardHeader className="min-w-0 shrink-0 [overflow-wrap:anywhere]">
                    <CardTitle className="flex items-center gap-2 text-lg">
                        <div className="p-2 bg-muted rounded-lg">
                            <ActivityIcon className="h-5 w-5 text-muted-foreground" />
                        </div>
                        Recent Activities
                    </CardTitle>
                    <CardDescription>Latest actions on your claims</CardDescription>
                </CardHeader>
                <CardContent className="min-h-0 min-w-0 flex-1 overflow-hidden [overflow-wrap:anywhere]">
                    <div className="space-y-3 h-full overflow-y-auto overflow-x-hidden pr-2">
                        {activities.length > 0 ? (
                            activities.map((activity) => {
                                const claimNumber = activity.metadata?.claimNumber as string | undefined;
                                const insuredName = activity.metadata?.insuredName as string | undefined;
                                // Use utility function to safely extract claimId
                                const claimId = getNotificationClaimId(activity);

                                return (
                                    <div
                                        key={activity.id}
                                        className="flex min-w-0 items-start gap-3 p-3 border border-border rounded-lg overflow-hidden hover:bg-muted/50 transition-colors duration-100 cursor-pointer group"
                                        onClick={() => claimId && setLocation(`/claims/${claimId}`)}
                                    >
                                        <div className="mt-0.5 shrink-0 transition-transform duration-100 group-hover:scale-110">
                                            {getSeverityIcon(activity.severity)}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-foreground line-clamp-3 [overflow-wrap:anywhere]" title={activity.message}>
                                                {activity.message}
                                            </p>
                                            {claimNumber && (
                                                <p className="text-xs text-muted-foreground mt-0.5 font-medium line-clamp-2 [overflow-wrap:anywhere]" title={`#${claimNumber}${insuredName ? ` • ${insuredName}` : ""}`}>
                                                    #{claimNumber} {insuredName && `• ${insuredName}`}
                                                </p>
                                            )}
                                            <p className="text-xs text-muted-foreground mt-1">
                                                {format(
                                                    new Date(activity.createdAt),
                                                    "MMM d, h:mm a"
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="h-full flex items-center justify-center">
                                <div className="text-center">
                                    <ActivityIcon className="h-12 w-12 text-muted-foreground mx-auto mb-2 opacity-20" />
                                    <p className="text-muted-foreground">No recent activities</p>
                                </div>
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>
        </motion.div>
    );
}
